import { Link } from "react-router-dom";
import { STAGES } from "@/content/journey";
import { CAPABILITIES } from "@/content/software";
import { ActionLink, Container, Eyebrow, Mono, Section, SectionTitle, TextLink } from "@/components/site/primitives";

const StageStrip = () => (
  <ol className="mt-14 grid grid-cols-1 gap-px border border-o-line bg-o-line sm:grid-cols-5" data-testid="home-stage-strip">
    {STAGES.map((s) => (
      <li key={s.id} className="bg-o-surface">
        <Link to={`/enterprise/diagnostic?stage=${s.id}#journey`} data-testid={`home-stage-${s.id}`}
          className="group flex h-full items-baseline gap-4 p-5 transition-colors hover:bg-o-soft sm:flex-col sm:gap-6">
          <Mono accent>{s.index}</Mono>
          <span>
            <span className="block font-display text-xl font-bold">{s.label}</span>
            <span className="mt-1 block text-sm text-o-dim">{s.summary}</span>
          </span>
        </Link>
      </li>
    ))}
  </ol>
);

const SoftwareList = ({ ids, label }) => (
  <div>
    <Mono>{label}</Mono>
    <ul className="mt-2 space-y-1">
      {ids.map((id) => (
        <li key={id}>
          <Link to={CAPABILITIES[id].path} state={{ via: "software" }} data-testid={`home-software-${id}`}
            className="flex items-baseline justify-between gap-4 border-b border-o-line py-2 transition-colors hover:text-o-accent">
            <span className="font-display font-bold">{CAPABILITIES[id].name}</span>
            <span className="text-xs text-o-dim">{CAPABILITIES[id].responsibility}</span>
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export const CommercialApplication = () => (
  <Section theme="t-paper" testid="home-enterprise" id="diagnostics-software">
    <Container className="py-24 lg:py-32">
      <Eyebrow index="03">Diagnostics & Software · For organizations</Eyebrow>
      <div className="mt-8 max-w-4xl">
        <SectionTitle>Tell us one problem your business keeps dealing with.</SectionTitle>
        <p className="mt-6 max-w-2xl leading-relaxed text-o-dim sm:text-lg">
          Organizations bring a real problem. Ordo Intelligence investigates what actually needs to change and, where it is warranted, delivers fitted software, architectural advisory, implementation, and continuing support.
        </p>
      </div>
      <StageStrip />
      <div className="mt-6 grid gap-4 lg:grid-cols-12">
        <div className="flex flex-col justify-between gap-8 border-2 border-o-accent bg-o-surface p-6 sm:p-8 lg:col-span-7" data-testid="home-diagnostic-card">
          <div>
            <Mono accent>Start with a problem</Mono>
            <h3 className="mt-3 font-display text-2xl font-bold sm:text-3xl">Ordo Diagnostic</h3>
            <p className="mt-3 max-w-lg text-o-dim">Purchased on its own. You decide afterwards whether anything is built, and you may stop after the diagnosis.</p>
          </div>
          <ActionLink to="/enterprise/diagnostic" testid="home-diagnostic-btn" className="self-start">See how the diagnostic works</ActionLink>
        </div>
        <div className="border border-o-line bg-o-raise p-6 sm:p-8 lg:col-span-5" data-testid="home-software-card">
          <Mono accent>Or go directly to software</Mono>
          <div className="mt-5 grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
            <SoftwareList ids={["engine", "stewardship"]} label="Ordo software family" />
            <SoftwareList ids={["breakpoint", "mesh"]} label="Independent offerings" />
          </div>
          <TextLink to="/enterprise/software" testid="home-software-all" className="mt-6">View all four in one place</TextLink>
        </div>
      </div>
    </Container>
  </Section>
);
